// Statements (SM-714 / SM-713): one currency's revenue statement for a
// window — the arithmetic (opening + earned + shares received +
// adjustments + reassigned in − reassigned out − payouts = closing), the
// closing breakdown by state, the supporting rows — and the CSV export of
// every row (a plain GET on the session cookie; broker-service writes a
// STATEMENT_EXPORTED audit event for it). The window, scope and page live
// in the URL so a statement link can be shared.
//
//   GET /api/broker/{id}/revenue/statement?currency=&from_unix_ms=&to_unix_ms=&scope=&offset=&limit=
//   GET /api/broker/{id}/revenue/statement.csv?currency=&from_unix_ms=&to_unix_ms=&scope=
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Money } from "@/components/ui/Money";
import { Id, Pager, TableFrame, Time } from "@/components/ui/Common";
import { EmptyState, QueryBlock } from "@/components/ui/States";
import { Segmented } from "@/components/ui/Field";
import { fetchStatement, keys, statementCsvUrl } from "@/lib/queries";
import { currentMonthUTC, dayRangeUTC, previousMonthUTC, utcToDateInput } from "@/lib/time";
import { formatBps } from "@/lib/units";
import type { RevenueStatement, RevenueStatementSection, StatementScope } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";
import { labelOf } from "@/i18n/labels";

interface StatementSearch {
  from?: number;
  to?: number;
  scope?: StatementScope;
  offset?: number;
}

export const Route = createFileRoute("/_app/statements")({
  validateSearch: (s: Record<string, unknown>): StatementSearch => ({
    from: typeof s.from === "number" && s.from > 0 ? s.from : undefined,
    to: typeof s.to === "number" && s.to > 0 ? s.to : undefined,
    scope: s.scope === "subtree" ? "subtree" : s.scope === "node" ? "node" : undefined,
    offset: typeof s.offset === "number" && s.offset > 0 ? s.offset : undefined,
  }),
  beforeLoad: capabilityGuard("FINANCE_VIEW"),
  component: StatementsPage,
});

const LIMIT = 50;

function StatementsPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { nodeId, currency } = useScope();
  const month = currentMonthUTC(Date.now());
  const from = search.from ?? month.from;
  const to = search.to ?? month.to;
  const scope: StatementScope = search.scope ?? "node";
  const params = { currency, from_unix_ms: from, to_unix_ms: to, scope, offset: search.offset ?? 0, limit: LIMIT };
  const probe = { queryKey: keys.statement(nodeId, params), queryFn: () => fetchStatement(nodeId, params) };
  const q = useQuery({ ...probe, enabled: guard.kind === "allow" });
  const csv = statementCsvUrl(nodeId, { currency, from_unix_ms: from, to_unix_ms: to, scope });
  const set = (patch: Partial<StatementSearch>) => void navigate({ search: (prev) => ({ ...prev, offset: undefined, ...patch }) });

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader
        title={t("statements.title")}
        subtitle={t("statements.subtitle")}
        coverage={scope === "subtree" ? "subtree" : "node"}
        money
        actions={
          <a href={csv} download className="btn btn-primary" rel="noopener">
            {t("statements.exportCsv")}
          </a>
        }
      />
      <SectionCard title={t("statements.period")} className="mb-4">
        <PeriodPicker key={`${from}-${to}`} from={from} to={to} onChange={(w) => set({ from: w.from, to: w.to })} />
        <div className="mt-3">
          <Segmented
            label={t("statements.scope")}
            value={scope}
            options={[
              { value: "node", label: t("statements.scopeNode") },
              { value: "subtree", label: t("statements.scopeSubtree") },
            ]}
            onChange={(v) => set({ scope: v })}
          />
        </div>
        <p className="mt-2 text-2xs text-t-3">
          {t("statements.windowDays", { from: utcToDateInput(from), to: utcToDateInput(to - 86_400_000) })}
        </p>
      </SectionCard>
      <QueryBlock query={q} lines={6}>
        {(st) => <Statement st={st} onOffset={(o) => set({ offset: o || undefined })} />}
      </QueryBlock>
    </Guarded>
  );
}

function PeriodPicker({ from, to, onChange }: { from: number; to: number; onChange: (w: { from: number; to: number }) => void }) {
  const { t } = useI18n();
  const [fromDay, setFromDay] = useState(utcToDateInput(from));
  const [toDay, setToDay] = useState(utcToDateInput(to - 86_400_000));
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Button type="button" variant="ghost" size="sm" onClick={() => onChange(currentMonthUTC(Date.now()))}>
        {t("statements.thisMonth")}
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onChange(previousMonthUTC(Date.now()))}>
        {t("statements.lastMonth")}
      </Button>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const w = dayRangeUTC(fromDay, toDay);
          if (!w) {
            setError(t("statements.badRange"));
            return;
          }
          setError(null);
          onChange(w);
        }}
      >
        <label className="flex flex-col gap-1 text-2xs font-semibold uppercase tracking-wide text-t-3">
          {t("statements.fromDay")}
          <input type="date" value={fromDay} onChange={(e) => setFromDay(e.target.value)} className="h-9 rounded-md border border-line-1 bg-bg-2 px-2 text-sm text-t-1" />
        </label>
        <label className="flex flex-col gap-1 text-2xs font-semibold uppercase tracking-wide text-t-3">
          {t("statements.toDay")}
          <input type="date" value={toDay} onChange={(e) => setToDay(e.target.value)} className="h-9 rounded-md border border-line-1 bg-bg-2 px-2 text-sm text-t-1" />
        </label>
        <Button type="submit" size="sm">
          {t("statements.apply")}
        </Button>
      </form>
      {error && (
        <p className="w-full text-2xs text-no" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function Statement({ st, onOffset }: { st: RevenueStatement; onOffset: (o: number) => void }) {
  const { t } = useI18n();
  const section = (st.sections ?? [])[0];
  return (
    <div className="space-y-4">
      {st.latest_recon_run_id && (
        <p className="text-2xs text-t-3">
          {t("statements.recon", { state: labelOf(t, "reconState", st.latest_recon_state) })} <Id value={st.latest_recon_run_id} /> ·{" "}
          {t("statements.generated")} <Time ms={st.generated_at_unix_ms} />
        </p>
      )}
      {section ? <Arithmetic s={section} /> : <EmptyState title={t("statements.noActivity")} />}
      <SectionCard title={t("statements.rows")}>
        {(st.rows ?? []).length === 0 ? (
          <EmptyState title={t("statements.noRows")} />
        ) : (
          <>
            <TableFrame label={t("statements.rows")}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th scope="col">{t("common.when")}</th>
                    <th scope="col">{t("statements.rowKind")}</th>
                    <th scope="col">{t("revenue.role")}</th>
                    <th scope="col">{t("statements.recipient")}</th>
                    <th scope="col">{t("statements.sourceOrRef")}</th>
                    <th scope="col" className="text-right">
                      {t("revenue.share")}
                    </th>
                    <th scope="col" className="text-right">
                      {t("statements.effect")}
                    </th>
                    <th scope="col">{t("common.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(st.rows ?? []).map((r) => (
                    <tr key={r.row_id}>
                      <td className="text-xs">
                        <Time ms={r.occurred_at_unix_ms} />
                      </td>
                      <td className="text-xs">{labelOf(t, "statementRow", r.kind)}</td>
                      <td className="text-xs">{r.role ? labelOf(t, "revenueRole", r.role.toLowerCase()) : "—"}</td>
                      <td>
                        <Id value={r.recipient_broker_id} />
                      </td>
                      <td>
                        <Id value={r.source_id || r.reference || r.row_id} />
                      </td>
                      <td className="num text-right">{r.share_bps ? formatBps(r.share_bps) : "—"}</td>
                      <td className="text-right">
                        <Money currency={r.currency} value={r.amount} tone="auto" />
                      </td>
                      <td className="text-xs">{labelOf(t, "earningState", r.state)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableFrame>
            <Pager page={st.page} onOffset={onOffset} />
          </>
        )}
      </SectionCard>
    </div>
  );
}

function Line({ label, value, strong, indent }: { label: string; value: ReactNode; strong?: boolean; indent?: boolean }) {
  return (
    <tr>
      <th scope="row" className={indent ? "pl-8 font-normal text-t-3" : strong ? "font-semibold text-t-1" : "font-medium text-t-2"}>
        {label}
      </th>
      <td className={strong ? "text-right font-semibold" : "text-right"}>{value}</td>
    </tr>
  );
}

function Arithmetic({ s }: { s: RevenueStatementSection }) {
  const { t } = useI18n();
  const c = s.currency;
  const m = (v: string | undefined, tone?: "auto") => <Money currency={c} value={v} tone={tone} />;
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <SectionCard title={t("statements.arithmetic")}>
        <TableFrame label={t("statements.arithmetic")}>
          <table className="tbl">
            <tbody>
              <Line label={t("statements.opening")} value={m(s.opening)} strong />
              <Line label={t("statements.earnedMaker")} value={m(s.earned?.maker)} />
              <Line label={t("statements.earnedTaker")} value={m(s.earned?.taker)} />
              <Line label={t("statements.earnedRedemption")} value={m(s.earned?.redemption)} />
              <Line label={t("statements.sharesMaker")} value={m(s.shares_received?.maker)} />
              <Line label={t("statements.sharesTaker")} value={m(s.shares_received?.taker)} />
              <Line label={t("statements.sharesRedemption")} value={m(s.shares_received?.redemption)} />
              <Line label={t("statements.adjustments")} value={m(s.adjustments, "auto")} />
              <Line label={t("statements.reassignedIn")} value={m(s.reassigned_in)} />
              <Line label={t("statements.reassignedOut")} value={m(s.reassigned_out)} />
              <Line label={t("statements.payouts")} value={m(s.payouts)} />
              <Line label={t("statements.closing")} value={m(s.closing)} strong />
            </tbody>
          </table>
        </TableFrame>
        <p className="mt-2 text-2xs text-t-3">{t("statements.arithmeticNote")}</p>
      </SectionCard>
      <SectionCard title={t("statements.closingBreakdown")}>
        <TableFrame label={t("statements.closingBreakdown")}>
          <table className="tbl">
            <tbody>
              <Line label={t("revenue.state.pending")} value={m(s.pending)} />
              <Line label={t("revenue.state.held")} value={m(s.held)} />
              <Line label={t("revenue.state.available")} value={m(s.available)} />
              <Line label={t("revenue.state.payoutReserved")} value={m(s.payout_reserved)} />
              <Line label={t("statements.closing")} value={m(s.closing)} strong />
            </tbody>
          </table>
        </TableFrame>
        <h3 className="section-title mb-2 mt-4">{t("statements.info")}</h3>
        <TableFrame label={t("statements.info")}>
          <table className="tbl">
            <tbody>
              <Line label={t("statements.tradedValue")} value={m(s.traded_value)} />
              <Line label={t("statements.exchangeFee")} value={m(s.exchange_fee)} />
              <Line label={t("statements.brokerCommission")} value={m(s.broker_commission)} />
              <Line label={t("statements.voidCommission")} value={m(s.void_commission)} />
            </tbody>
          </table>
        </TableFrame>
        <p className="mt-2 text-2xs text-t-3">{t("statements.infoNote")}</p>
      </SectionCard>
    </div>
  );
}
